import { createFeatureSelector, createSelector } from '@ngrx/store';
import { journalFeatureKey, JournalState } from '../_reducers/journal.reducer';
import { selectLoggedUser } from 'app/_selectors/user.selectors';
import { replaceUrlParams } from '../_reducers/replaceUserParams.fn';
import { JournalSettingsKeys } from '../../../settings/src/lib/journal-settings/journal-settings.defaults2';
import { SettingsEnum } from 'app/_interfaces/settings.interface';
import { selectEntities } from '../_reducers/entry-type-entity.reducer';

export const selectJournalState = createFeatureSelector<JournalState>(journalFeatureKey);
export const selectInitJournal = createSelector(selectJournalState, state => state.init);
export const selectOriginalEntries = createSelector(selectJournalState, state => state.rawEntries);
export const selectConfig = createSelector(selectJournalState, state => state.config);
export const selectByList = createSelector(selectJournalState, state => state.byList);
export const selectPrintModeByList = createSelector(selectJournalState, state => state.byList.printMode);
export const selectPagedEntriesByList = createSelector(selectJournalState, state => state.byList.entriesPerPage);
export const selectCitizenId = createSelector(selectJournalState, state => state.citizenId);
export const selectCitizen = createSelector(selectJournalState, state => state.citizen);
export const selectUrlToGetFile = createSelector(
	selectLoggedUser,
	selectCitizenId,
	(user, citizenId) => replaceUrlParams(`api/clinics/{clinicId}/patients/${citizenId}/attachments`, user)
);

/***
 * TOGGLE OVERVIEW
 */
export const selectDrawingsColumnVisibility = createSelector(selectJournalState, state => state.drawingsColumnVisible);
export const selectVisibleEntryIds = createSelector(selectJournalState, (state) => state.visibleEntryIds);
export const selectOverviewEntriesLimit = createSelector(selectJournalState, state => state.overviewEntriesLimit);
export const selectFoldedAllEntries = createSelector(selectJournalState, state => state.foldedAll);
export const selectExtendedEntryTypes = createSelector(selectJournalState, state => state.extendedEntryTypes);
export const selectExtendedEntryTypeIds = createSelector(
	selectEntities,
	selectExtendedEntryTypes,
	(entities, types) => types.map(type =>
		Object.values(entities).find(e =>
			e?.entryType.toLowerCase() === type.toLowerCase()
		)?.entryTypeId
	)
);
export const selectUnfoldContent = createSelector(selectJournalState, state => state.unfoldContent);
export const selectFoldContentLinesNumber = createSelector(selectJournalState, state => state.foldContentLinesNumber);

/***
 * OPENING SINGLE ENTRY IN OVERVIEW
 */
export const selectEntryIdForOpen = createSelector(selectJournalState, state => state.scrollToWarningField.entryIdForOpen);
export const selectFieldToScrollTo = createSelector(selectJournalState, state => state.scrollToWarningField.scrollToFieldId);

/***
 * DRAWINGS OVERVIEW
 */
export const selectDrawingsBigSize = createSelector(selectJournalState, state => state.byDrawings.bigSize);
export const selectDrawingsDescriptions = createSelector(selectJournalState, state => state.byDrawings.descriptions);
export const selectEntriesColumnVisibility = createSelector(selectJournalState, state => state.entriesColumnVisible);
export const selectSelectedPlacedSymbolId = createSelector(selectJournalState, state => state.placedSymbolSelected);
export const selectActiveSymbolsVisibility = createSelector(selectJournalState, state => state.byDrawings.showInactiveSymbols);
export const selectHiddenSchemaIds = createSelector(selectJournalState, state => state.byDrawings.hiddenSchemaIds);

/***
 * JOURNAL SETTINGS
 */
export const selectJournalSettingsState = createSelector(selectJournalState, (state) => state.settings);
export const selectAppAndUserLoadedSettings = createSelector(
	selectJournalSettingsState,
	(state) => state.appLoaded && state.userLoaded
);
export const selectGeneralSettings = createSelector(
	selectJournalSettingsState,
	(state) => state.generalSettings
);
export const selectJournalSettings = createSelector(
	selectJournalSettingsState,
	(state) => state.journalSettings
);
export const selectJournalSettingByKey = (key: JournalSettingsKeys) => createSelector(
	selectJournalSettingsState,
	(settings) => {
		if (!settings.journalSettings) return null;
		return settings.journalSettings[`${SettingsEnum.journal}.${key}`];
	}
);
export const selectEntryTypeExtended = createSelector(
	selectJournalSettingsState,
	(state) => state.journalSettings?.[`${SettingsEnum.journal}.${JournalSettingsKeys.entryTypeExtended}`]
);

export const selectCorruptedEntriesConsent = createSelector(selectJournalState, state => state.corruptedEntriesConsent);
export const selectSelectedTemplateIds = createSelector(selectJournalState, state => state.selectedTemplateIds);
export const selectSelectedAuthorIds = createSelector(selectJournalState, state => state.selectedAuthorIds);
export const selectSelectedDateRange = createSelector(selectJournalState, state => state.selectedDateRange);
export const selectSearchKeyword = createSelector(selectJournalState, state => state.searchKeyword);
